import React, { createContext, useContext, useReducer, useEffect } from 'react'
import PropTypes from 'prop-types'

const ProductContext = createContext()

const initialState = {
  products: [],
  categories: [],
  featuredProducts: [],
  isLoading: false,
  error: null,
  filters: {
    category: '',
    priceRange: [0, 1000],
    sortBy: 'name',
    searchQuery: '',
  },
  pagination: {
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 12,
  },
}

const productReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    
    case 'SET_PRODUCTS':
      return { ...state, products: action.payload }
    
    case 'SET_CATEGORIES':
      return { ...state, categories: action.payload }
    
    case 'SET_FEATURED_PRODUCTS':
      return { ...state, featuredProducts: action.payload }
    
    case 'UPDATE_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
        pagination: { ...state.pagination, currentPage: 1 }, // Reset to first page
      }
    
    case 'SET_PAGINATION':
      return { ...state, pagination: { ...state.pagination, ...action.payload } }
    
    case 'CLEAR_FILTERS':
      return {
        ...state,
        filters: initialState.filters,
        pagination: { ...state.pagination, currentPage: 1 },
      }
    
    default:
      return state
  }
}

export const ProductProvider = ({ children }) => {
  const [state, dispatch] = useReducer(productReducer, initialState)

  const setLoading = (loading) => {
    dispatch({ type: 'SET_LOADING', payload: loading })
  }

  const setError = (error) => {
    dispatch({ type: 'SET_ERROR', payload: error })
  }

  const fetchProducts = async (filters = {}) => {
    setLoading(true)
    setError(null)
    
    try {
      const queryParams = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          queryParams.append(key, value)
        }
      })
      
      const response = await fetch(`https://maurifood-api.hansye11.workers.dev/api/products?${queryParams}`)
      if (response.ok) {
        const data = await response.json()
        dispatch({ type: 'SET_PRODUCTS', payload: data.products })
        dispatch({ type: 'SET_PAGINATION', payload: data.pagination })
      } else {
        throw new Error('Failed to fetch products')
      }
    } catch (error) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await fetch('https://maurifood-api.hansye11.workers.dev/api/categories')
      if (response.ok) {
        const responseData = await response.json()
        // Handle both direct array and wrapped response formats
        const categories = Array.isArray(responseData) ? responseData : responseData.value || responseData
        dispatch({ type: 'SET_CATEGORIES', payload: categories })
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error)
    }
  }

  const fetchFeaturedProducts = async () => {
    try {
      const response = await fetch('https://maurifood-api.hansye11.workers.dev/api/products?featured=true')
      if (response.ok) {
        const products = await response.json()
        dispatch({ type: 'SET_FEATURED_PRODUCTS', payload: products })
      }
    } catch (error) {
      console.error('Failed to fetch featured products:', error)
    }
  }

  const updateFilters = (newFilters) => {
    dispatch({ type: 'UPDATE_FILTERS', payload: newFilters })
  }

  const setPagination = (pagination) => {
    dispatch({ type: 'SET_PAGINATION', payload: pagination })
  }

  const clearFilters = () => {
    dispatch({ type: 'CLEAR_FILTERS' })
  }

  const searchProducts = async (query) => {
    if (!query.trim()) {
      await fetchProducts()
      return
    }
    
    updateFilters({ searchQuery: query })
    await fetchProducts({ ...state.filters, searchQuery: query })
  }

  // Load initial data
  useEffect(() => {
    fetchProducts()
    fetchCategories()
    fetchFeaturedProducts()
  }, [])

  const value = {
    ...state,
    fetchProducts,
    fetchCategories,
    fetchFeaturedProducts,
    updateFilters,
    setPagination,
    clearFilters,
    searchProducts,
  }

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>
}

ProductProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useProducts = () => {
  try {
    const context = useContext(ProductContext)
    if (!context) {
      console.warn('useProducts must be used within a ProductProvider')
      // Return a default context to prevent crashes
      return {
        products: [],
        categories: [],
        featuredProducts: [],
        isLoading: false,
        error: null,
        filters: {
          category: '',
          priceRange: [0, 1000],
          sortBy: 'name',
          searchQuery: '',
        },
        pagination: {
          currentPage: 1,
          totalPages: 1,
          itemsPerPage: 12,
        },
        fetchProducts: () => {},
        fetchCategories: () => {},
        fetchFeaturedProducts: () => {},
        setFilters: () => {},
        setPagination: () => {},
        clearError: () => {},
        dispatch: () => {}
      }
    }
    return context
  } catch (error) {
    console.error('ProductContext error:', error)
    return {
      products: [],
      categories: [],
      featuredProducts: [],
      isLoading: false,
      error: null,
      filters: {
        category: '',
        priceRange: [0, 1000],
        sortBy: 'name',
        searchQuery: '',
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        itemsPerPage: 12,
      },
      fetchProducts: () => {},
      fetchCategories: () => {},
      fetchFeaturedProducts: () => {},
      setFilters: () => {},
      setPagination: () => {},
      clearError: () => {},
      dispatch: () => {}
    }
  }
}
